import {Chip} from '@dropbox/dig-components/chip';
import {FormRow} from '@dropbox/dig-components/form_row';
import {TextInput, TextInputInputRefObject} from '@dropbox/dig-components/text_fields';
import {Typeahead} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {SearchLine} from '@dropbox/dig-icons/assets';
import {usePeopleTypeaheadAnalytics} from 'analytics/usePeopleTypeaheadAnalytics';
import {pulseUserAtom} from 'atoms/auth';
import {loggedInEmployeeAtom} from 'atoms/employee';
import classNames from 'classnames';
import {Employee} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {isDev} from 'helpers/environment';
import {PEOPLE_SEARCH_RANKING_MODE, useSearchesWithStatus} from 'hooks/useEmployee';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {FunctionComponent, useEffect, useMemo, useRef, useState} from 'react';

import styles from './PeopleSearchMenu.module.css';

interface Props {
  selectedEmployees: Employee[];
  supportCSV?: boolean;
  onSelectEmployee: (employee: Employee) => void;
  onSelectEmployees: (employee: Employee[]) => void;
  onRemoveEmployee: (employee: Employee) => void;
  className?: string;
  inputClassName?: string;
  autoFocus?: boolean;
  placeholderText?: string;
  disable?: boolean;
  allowSelf?: boolean;
  showReportingLine?: boolean;
  minHeight?: string;
  isSingleSelect?: boolean;
  searchIconPosition?: 'left' | 'right';
  isInvalid?: boolean;
  preferredEmployees?: Employee[];
  preferredEmployeeIds?: string[];
  sortEmployees?: (a: Employee, b: Employee) => number;
  typeaheadContext?: Parameters<typeof usePeopleTypeaheadAnalytics>[0]['context'];
}

export const PeopleSearchMenu: FunctionComponent<Props> = ({
  autoFocus = false,
  selectedEmployees,
  onSelectEmployee,
  onSelectEmployees,
  onRemoveEmployee,
  placeholderText = t('search_dropboxers'),
  className,
  inputClassName,
  disable = false,
  allowSelf = false,
  showReportingLine = true,
  isSingleSelect = false,
  searchIconPosition = 'right',
  minHeight,
  isInvalid,
  preferredEmployees,
  preferredEmployeeIds,
  sortEmployees,
  typeaheadContext = 'people_search_menu',
}) => {
  const inputContainerRef = useRef<HTMLElement | null>(null);
  const textInputRef = useRef<HTMLElement | null>(null);
  const {reportingLine} = useAtomValue(loggedInEmployeeAtom);
  const [userInputValue, setUserInputValue] = useState<string>('');
  const [hasExpandedResults, setHasExpandedResults] = useState(false);
  const [resultsResetKey, setResultsResetKey] = useState(0);
  const resetVisibleResults = () => {
    setHasExpandedResults(false);
    setResultsResetKey((key) => key + 1);
  };
  const [inputTextActive, setInputTextActive] = useState<boolean>(false);

  const rankingMode = PEOPLE_SEARCH_RANKING_MODE;
  const {results: searchResult, isLoading} = useSearchesWithStatus({
    ldaps:
      userInputValue === '' && showReportingLine
        ? undefined
        : userInputValue.split(',').map((e) => (!e.includes('@') ? e : e.trim().split('@')[0])),
  });

  const user = useAtomValue(pulseUserAtom);
  const reportingLineWithoutSelf = allowSelf
    ? reportingLine
    : reportingLine.length > 1
      ? reportingLine.slice(1)
      : reportingLine;

  const preferredEmployeesById = useMemo(
    () =>
      new Set(
        (preferredEmployeeIds ?? preferredEmployees?.map(({user_id}) => user_id) ?? []).filter(
          Boolean
        )
      ),
    [preferredEmployeeIds, preferredEmployees]
  );

  const baseEmployees = useMemo(() => {
    if (!userInputValue.length) {
      if (preferredEmployees) {
        return [...preferredEmployees];
      }

      if (showReportingLine) {
        return [...reportingLineWithoutSelf];
      }
    }

    return searchResult.length ? [...searchResult] : [];
  }, [
    preferredEmployees,
    reportingLineWithoutSelf,
    searchResult,
    showReportingLine,
    userInputValue.length,
  ]);

  const employeeHints = useMemo(() => {
    const filteredEmployees = baseEmployees.filter((employee) => {
      const isSelected = selectedEmployees.some((selected) => selected.ldap === employee.ldap);
      if (isSelected) return false;

      if (isDev) return true;
      if (allowSelf) return true;
      return employee.ldap !== user?.email.split('@')[0];
    });

    if (!userInputValue.length && sortEmployees) {
      filteredEmployees.sort(sortEmployees);
    }

    if (userInputValue.length > 0 && preferredEmployeesById.size > 0) {
      const prioritized: Employee[] = [];
      const remaining: Employee[] = [];

      filteredEmployees.forEach((employee) => {
        if (preferredEmployeesById.has(employee.user_id)) {
          prioritized.push(employee);
        } else {
          remaining.push(employee);
        }
      });

      if (sortEmployees) {
        prioritized.sort(sortEmployees);
      }

      return [...prioritized, ...remaining];
    }

    return filteredEmployees;
  }, [
    allowSelf,
    baseEmployees,
    preferredEmployeesById,
    selectedEmployees,
    sortEmployees,
    user?.email,
    userInputValue.length,
  ]);
  const analytics = usePeopleTypeaheadAnalytics({
    context: typeaheadContext,
    input: userInputValue,
    results: employeeHints,
    isLoading,
    rankingMode,
    visibleResultCount: hasExpandedResults ? 8 : 4,
  });

  const handleSelection = (ldap: string, event?: {type?: string}) => {
    const found = employeeHints.find((e) => e.ldap === ldap);
    if (found) {
      analytics.select(found, event?.type === 'keydown' ? 'keyboard' : 'click');
      onSelectEmployee(found);
      analytics.commit();
      resetVisibleResults();
      setUserInputValue('');
      //make sure to not focus on the input after selecting an employee
      if (textInputRef.current && isSingleSelect) {
        textInputRef.current?.blur();
      }
    }
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    resetVisibleResults();
    setUserInputValue(event.currentTarget.value);
  };

  const onInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    event.stopPropagation();
  };

  const renderSubtitle = (result?: string | null) => {
    return (
      <>
        <Text size="small" color="faint">
          {result}
        </Text>
      </>
    );
  };

  const onToggle = ({isOpen}: {isOpen: boolean}) => {
    // We can even add a chip during menu close!
    setInputTextActive(isOpen);
  };

  useEffect(() => {
    if (userInputValue.length > 0 && userInputValue.includes(',') && searchResult.length) {
      const foundEmployees = userInputValue.split(',').reduce((acc, email) => {
        const found = employeeHints.find((e) => e.ldap === email.trim().split('@')[0]);
        if (found) {
          setUserInputValue((currentValue) => currentValue.replace(email, ''));
          acc.push(found);
        }
        return acc;
      }, [] as Employee[]);

      if (foundEmployees.length) {
        foundEmployees.forEach((employee) => analytics.select(employee));
        onSelectEmployees(foundEmployees);
        analytics.commit();
        setUserInputValue((currentValue) =>
          currentValue
            .split(',')
            .filter((e) => e.length)
            .join(',')
        );
        resetVisibleResults();
      }
    }
  }, [analytics, employeeHints, onSelectEmployees, searchResult, userInputValue]);

  useEffect(() => {
    // Add a listener to keyboard delelte key to remove the last selected employee
    const handleKeyDown = (event: KeyboardEvent) => {
      // check if the text input is active or not
      if (!inputTextActive) {
        return;
      }
      if (event.key === 'Backspace' && userInputValue === '') {
        onRemoveEmployee(selectedEmployees[selectedEmployees.length - 1]);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onRemoveEmployee, selectedEmployees, userInputValue, inputTextActive]);

  const renderTypeaheadRow = (employee: Employee) => {
    return (
      <Typeahead.Row
        key={employee.user_id}
        value={employee.ldap}
        withTitle={<Text isBold>{employee.name}</Text>}
        withSubtitle={renderSubtitle(employee.role)}
        disabled={disable}
        withLeftAccessory={<Avatar user={employee} />}
      />
    );
  };
  return (
    <FormRow>
      <Typeahead.Wrapper
        className={classNames(className, {
          [styles.chips]: selectedEmployees.length > 0,
        })}
        onSelection={handleSelection}
        onToggle={(state) => {
          onToggle(state);
          if (state.isOpen) analytics.start();
          else {
            analytics.dismiss();
            resetVisibleResults();
          }
        }}
      >
        {({getTriggerProps, getContentProps}) => (
          <>
            <TextInput.Container
              className={inputClassName}
              isInvalid={isInvalid}
              ref={inputContainerRef}
              style={{minHeight: minHeight, maxHeight: minHeight}}
            >
              {searchIconPosition === 'left' && (
                <Box style={{paddingRight: '4px', marginLeft: '-4px'}}>
                  <TextInput.Accessory>
                    <UIIcon src={SearchLine} />
                  </TextInput.Accessory>
                </Box>
              )}
              <TextInput.ChipsContainer>
                {!!selectedEmployees.length &&
                  selectedEmployees.map((employee) => (
                    <Chip
                      disabled={disable}
                      key={`delegate-chip-${employee.user_id}`}
                      onDelete={() => {
                        onRemoveEmployee(employee);
                        if (textInputRef.current) {
                          textInputRef.current.focus();
                        }
                      }}
                      size={'small'}
                    >
                      <Chip.AvatarAccessory>
                        <Avatar user={employee} />
                      </Chip.AvatarAccessory>
                      <Chip.Content>{employee.name}</Chip.Content>
                    </Chip>
                  ))}

                <TextInput.Input
                  {...getTriggerProps({
                    onChange: onInputChange,
                    onKeyDown: onInputKeyDown,
                    onFocus: analytics.start,
                  })}
                  placeholder={selectedEmployees.length === 0 ? placeholderText.toString() : ''}
                  value={userInputValue}
                  autoFocus={autoFocus}
                  disabled={disable}
                  ref={textInputRef as React.RefObject<TextInputInputRefObject>}
                  readOnly={isSingleSelect && selectedEmployees.length === 1}
                />
                {searchIconPosition === 'right' && (
                  <TextInput.Accessory>
                    <UIIcon src={SearchLine} />
                  </TextInput.Accessory>
                )}
              </TextInput.ChipsContainer>
            </TextInput.Container>
            <Typeahead.Container {...getContentProps()} triggerRef={inputContainerRef}>
              {employeeHints.length > 0 ? (
                <Typeahead.Results
                  key={resultsResetKey}
                  size="large"
                  initialResults={hasExpandedResults ? 8 : 4}
                  maxResults={8}
                  showMoreOnClick={() => {
                    setHasExpandedResults(true);
                    analytics.showMore();
                  }}
                  results={employeeHints}
                  renderRow={renderTypeaheadRow}
                />
              ) : userInputValue.trim().length > 0 ? (
                <Typeahead.Prompt>
                  <Text color="faint">{t('no_dropboxer')}</Text>
                </Typeahead.Prompt>
              ) : null}
            </Typeahead.Container>
          </>
        )}
      </Typeahead.Wrapper>
    </FormRow>
  );
};
